import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { recordLoginEvent } from '../hooks/useSecurity'
import { Spinner, VStack, Text } from 'wss3-forge'

// Extract profile data from OAuth metadata
function extractOAuthProfileData(meta: Record<string, any>) {
  const iss = meta?.iss || ''

  let firstName = null
  let lastName = null
  let displayName = null
  let avatarUrl = null

  // Discord
  if (iss.includes('discord')) {
    displayName = meta?.custom_claims?.global_name || meta?.full_name
    avatarUrl = meta?.avatar_url
  }
  // Google
  else if (iss.includes('google')) {
    firstName = meta?.given_name
    lastName = meta?.family_name
    displayName = meta?.name
    avatarUrl = meta?.picture
  }
  // Microsoft/Azure
  else if (iss.includes('microsoft') || iss.includes('login.microsoftonline')) {
    firstName = meta?.given_name
    lastName = meta?.family_name
    displayName = meta?.name
    avatarUrl = meta?.picture
  }
  // GitHub
  else if (iss.includes('github') || meta?.user_name) {
    displayName = meta?.name || meta?.user_name
    avatarUrl = meta?.avatar_url
    if (displayName && displayName.includes(' ')) {
      const parts = displayName.split(' ')
      firstName = parts[0]
      lastName = parts.slice(1).join(' ')
    }
  }
  // Fallback
  else {
    displayName = meta?.name || meta?.full_name
    avatarUrl = meta?.avatar_url || meta?.picture
  }

  return { firstName, lastName, displayName, avatarUrl }
}

export function AuthCallback() {
  const navigate = useNavigate()

  useEffect(() => {
    const handleCallback = async () => {
      const { data: { session }, error } = await supabase.auth.getSession()

      if (error || !session) {
        console.error('Auth callback error:', error)
        navigate('/login?error=auth_failed')
        return
      }

      // Create/update user profile with OAuth data
      try {
        const meta = session.user.user_metadata
        const { firstName, lastName, displayName, avatarUrl } = extractOAuthProfileData(meta)

        // Upsert profile
        await supabase.from('user_profiles').upsert({
          user_id: session.user.id,
          first_name: firstName,
          last_name: lastName,
          display_name: displayName,
          avatar_url: avatarUrl
        }, {
          onConflict: 'user_id',
          ignoreDuplicates: false
        })
      } catch (err) {
        console.error('Failed to sync profile:', err)
        // Don't block login if profile sync fails
      }

      // Record OAuth login event
      const provider = session.user.app_metadata?.provider || 'oauth'
      await recordLoginEvent(session.user.id, `oauth_${provider}`)

      // Check if we have stored redirect info from OAuth flow
      const appId = sessionStorage.getItem('webba_auth_app_id')
      const redirectUri = sessionStorage.getItem('webba_auth_redirect_uri')

      if (appId && redirectUri) {
        // Clear stored data
        sessionStorage.removeItem('webba_auth_app_id')
        sessionStorage.removeItem('webba_auth_redirect_uri')

        try {
          // Generate auth code
          const { data: code, error: codeError } = await supabase.rpc('generate_auth_code', {
            p_user_id: session.user.id,
            p_app_id: appId,
            p_redirect_uri: redirectUri
          })

          if (codeError) throw codeError

          // Redirect to app with code
          const url = new URL(redirectUri)
          url.searchParams.set('code', code)
          window.location.href = url.toString()
          return
        } catch (err) {
          console.error('Failed to generate auth code:', err)
          navigate('/login?error=code_generation_failed')
          return
        }
      }

      // No redirect, go to home
      navigate('/')
    }

    handleCallback()
  }, [navigate])

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'var(--bg-primary)'
    }}>
      <VStack gap="md" style={{ alignItems: 'center' }}>
        <Spinner size="lg" />
        <Text color="muted">Signing you in...</Text>
      </VStack>
    </div>
  )
}
